/** Full-page Google authorization-code flow. The browser stores only short-lived
 * PKCE/CSRF material; the platform Worker holds the client secret and exchanges
 * the code. This works in mobile system browsers, including iOS Safari. */

const PENDING_KEY = 'dsp.google.redirect';
const MAX_AGE_MS = 10 * 60 * 1000;

export interface GoogleRedirectConfig {
  clientId: string;
  redirectUri: string;
}

interface PendingRedirect {
  state: string;
  nonce: string;
  codeVerifier: string;
  returnTo: string;
  createdAt: number;
}

function randomBase64Url(bytes: number): string {
  const data = crypto.getRandomValues(new Uint8Array(bytes));
  return btoa(String.fromCharCode(...data))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function sha256Base64Url(value: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Starts in the same tab so sessionStorage survives Google's redirect. */
export async function startGoogleRedirect(
  config: GoogleRedirectConfig,
  returnTo: string
): Promise<void> {
  const callback = new URL(config.redirectUri);
  if (
    callback.origin !== window.location.origin ||
    callback.pathname !== '/auth/google/callback' ||
    callback.search ||
    callback.hash
  ) {
    throw new Error('Google sign-in is not configured for this site.');
  }
  const pending: PendingRedirect = {
    state: randomBase64Url(32),
    nonce: randomBase64Url(32),
    codeVerifier: randomBase64Url(64),
    returnTo,
    createdAt: Date.now(),
  };
  const challenge = await sha256Base64Url(pending.codeVerifier);
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  url.search = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    state: pending.state,
    nonce: pending.nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  }).toString();
  window.location.assign(url.toString());
}

/** Consume the one-time browser challenge before calling the token exchange. */
export function consumeGoogleRedirect(search: string): {
  code: string;
  codeVerifier: string;
  nonce: string;
  returnTo: string;
} {
  const query = new URLSearchParams(search);
  const stored = sessionStorage.getItem(PENDING_KEY);
  sessionStorage.removeItem(PENDING_KEY);
  if (query.has('error')) throw new Error('Google sign-in was cancelled.');
  if (!stored) throw new Error('Google sign-in expired. Please start again.');
  let pending: PendingRedirect;
  try {
    pending = JSON.parse(stored) as PendingRedirect;
  } catch {
    throw new Error('Google sign-in expired. Please start again.');
  }
  if (
    !pending.state ||
    !pending.nonce ||
    !pending.codeVerifier ||
    Date.now() - pending.createdAt > MAX_AGE_MS ||
    Date.now() < pending.createdAt ||
    query.get('state') !== pending.state ||
    !query.get('code')
  ) {
    throw new Error('Google sign-in could not be verified. Please start again.');
  }
  return {
    code: query.get('code')!,
    codeVerifier: pending.codeVerifier,
    nonce: pending.nonce,
    returnTo: pending.returnTo,
  };
}
